import React from "react";

/**
 * Pakistani truck-art pattern band (inspired by the colourful packaging
 * shown in the Hair Drips brand kit). Repeating SVG tile in the six brand
 * colours: hot pink, orange, purple, teal, yellow, and a deep magenta.
 *
 * Renders crisp at any width because it uses SVG <pattern>.
 *
 * Props:
 *   - flip:    if true, the band is mirrored vertically (useful for the
 *              bottom of a section so the pattern points outward)
 *   - height:  tailwind-friendly height class (default h-7)
 *   - className: extra classes
 */
export const TruckArtBand = ({ flip = false, className = "", height = "h-7" }) => {
  // Tile dimensions
  const W = 80; // tile width in viewBox units
  const H = 40; // tile height

  const colors = {
    pink: "#e5197f",
    orange: "#f26b1e",
    purple: "#7a3cff",
    teal: "#1ba0c2",
    yellow: "#ffd24a",
    magenta: "#b5158d",
    cream: "#fff8f0",
  };

  return (
    <div
      aria-hidden="true"
      className={`relative w-full ${height} overflow-hidden ${className}`}
      style={{ transform: flip ? "scaleY(-1)" : undefined }}
      data-testid={`truck-art-band-${flip ? "bottom" : "top"}`}
    >
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox={`0 0 ${W * 4} ${H}`}
        preserveAspectRatio="none"
      >
        <defs>
          <pattern
            id={`tap-${flip ? "b" : "t"}`}
            x="0"
            y="0"
            width={W}
            height={H}
            patternUnits="userSpaceOnUse"
          >
            {/* base alternating chevrons */}
            <polygon points={`0,${H} ${W / 4},0 ${W / 2},${H}`} fill={colors.teal} />
            <polygon points={`${W / 2},${H} ${(W * 3) / 4},0 ${W},${H}`} fill={colors.purple} />
            {/* inner chevrons (overlapping smaller triangles) */}
            <polygon
              points={`${W / 16},${H} ${W / 4},${H * 0.25} ${(W * 7) / 16},${H}`}
              fill={colors.pink}
            />
            <polygon
              points={`${(W * 9) / 16},${H} ${(W * 3) / 4},${H * 0.25} ${(W * 15) / 16},${H}`}
              fill={colors.orange}
            />
            {/* yellow stripe at bottom */}
            <rect x="0" y={H - 6} width={W} height="3" fill={colors.yellow} />
            {/* tiny diamonds */}
            <g fill={colors.cream}>
              <polygon
                points={`${W / 4},${H * 0.55} ${W / 4 + 4},${H * 0.7} ${W / 4},${H * 0.85} ${W / 4 - 4},${H * 0.7}`}
              />
              <polygon
                points={`${(W * 3) / 4},${H * 0.55} ${(W * 3) / 4 + 4},${H * 0.7} ${(W * 3) / 4},${H * 0.85} ${(W * 3) / 4 - 4},${H * 0.7}`}
              />
            </g>
            {/* dots row */}
            <g fill={colors.magenta}>
              <circle cx={W * 0.125} cy={H - 12} r="1.6" />
              <circle cx={W * 0.375} cy={H - 12} r="1.6" />
              <circle cx={W * 0.625} cy={H - 12} r="1.6" />
              <circle cx={W * 0.875} cy={H - 12} r="1.6" />
            </g>
          </pattern>
        </defs>
        <rect x="0" y="0" width={W * 4} height={H} fill={`url(#tap-${flip ? "b" : "t"})`} />
      </svg>
    </div>
  );
};

export default TruckArtBand;
